import React, { useState } from 'react'
import styled from '@emotion/styled'
import ScrollableList from 'modules/ui/molecules/ScrollableList'
import Label from 'modules/ui/atoms/Typography/Label'
import { DEFAULT_FONT_STYLES, DEFAULT_THEME_COLORS } from 'css/theme'
import { ScrollableListData } from 'modules/ui/molecules/ScrollableList'
import { useTranslation } from 'next-i18next'
import useDoubleTheDonationCompaniesSearch from '../../../../../../order/components/double-the-donation/use-double-the-donation-companies-search'
import { Collapsible } from '../../../../../../ui/atoms/Collapsible'
import useOrder from '../../../../../../order/state/order/use-order'

import mParticleSdk from '@mparticle/web-sdk'
import useTrackUIElementEvent from 'modules/analytics/hooks/useTrackUIElementEvent'

const EmployerMatchingContainer = styled.fieldset`
  border: none;
  margin: 0;
  padding: 0;
`

const ChooseAnotherButton = styled.div({
  color: 'rgba(46, 94, 217, 1)',
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'flex-start',
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontSize: DEFAULT_FONT_STYLES['--theme-font--body-sm__font-size'],
  lineHeight: '21px',
  fontWeight: DEFAULT_FONT_STYLES['--theme-font--body-default__font-weight'],
  textDecoration: 'underline',
})

const EmployerMatching = () => {
  const { t } = useTranslation()
  const trackUIElementEvent = useTrackUIElementEvent()
  const order = useOrder()

  const [query, setQuery] = useState('')
  const [pageNumber, setPageNumber] = useState(1)
  const [selected, setSelected] = useState<string>(order.doubleTheDonation.value?.companyName ?? '')

  const { data, companies, hasMore, loading, error } = useDoubleTheDonationCompaniesSearch(
    query,
    pageNumber,
  )

  const clearSelection = () => {
    setSelected('')
    order.doubleTheDonation.clearValue()
  }

  const handleSelect = ({ value, label }: ScrollableListData) => {
    setSelected(label)
    const company = companies.find((c) => c.id === Number(value))

    company
      ? order.doubleTheDonation.setValue({ companyId: value, companyName: company.company_name })
      : order.doubleTheDonation.clearValue()
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') clearSelection()
  }

  const handleFetchNewData = (newQuery: string, newPage: number) => {
    setQuery(newQuery)
    setPageNumber(newPage)
  }

  const handleCollapsibleToggle = (isOpen: boolean) => {
    // Track employer matching toggle event
    trackUIElementEvent('Value Selected', mParticleSdk.EventType.Other, {
      tracking_id: 'employer matching start',
      value: isOpen ? 'enabled' : 'disabled',
    })
  }

  return (
    <Collapsible
      color="#EBEAEA"
      iconName="user_heart"
      label={t('orderDoubleTheDonationSelector.employerMatching.label', 'Employer Matching')}
      dataId="employer_matching"
      onCollapsibleToggle={handleCollapsibleToggle}
    >
      <EmployerMatchingContainer>
        {selected ? (
          <>
            <Label size="default" color={DEFAULT_THEME_COLORS['--theme-color--neutral-10']}>
              {t('orderDoubleTheDonationSelector.selectedCompany.label', 'Company: {{selected}}', {
                selected,
              })}
            </Label>
            <ChooseAnotherButton
              tabIndex={0}
              role="button"
              onClick={clearSelection}
              onKeyDown={handleKeyDown}
              data-id="double-the-donation-drawer-choose-another-button"
            >
              {t(
                'orderDoubleTheDonationSelector.selectDifferentCompany.label',
                'Select a different company',
              )}
            </ChooseAnotherButton>
          </>
        ) : (
          <ScrollableList
            data={data}
            selected={selected}
            isActivatedSearch
            hasMore={hasMore}
            loading={loading}
            error={error}
            height={300}
            isPopUpResult
            fetchNewData={handleFetchNewData}
            onSelect={handleSelect}
            dataId="double-the-donation-drawer"
          />
        )}
      </EmployerMatchingContainer>
    </Collapsible>
  )
}

export default EmployerMatching
